import {
  autocomplete,
  placeDetails,
  createAddress,
  type AutocompletePrediction,
  type PlaceDetails,
  type Address,
  type AddressDetails,
  type AddressComponent,
  type CreateAddressRequest,
} from '@vn-propane/shared/client';

/**
 * Fetch autocomplete predictions for an address search
 */
export async function getAutocomplete(
  orgId: string,
  input: string,
  options?: {
    country?: string;
    sessionToken?: string;
    lat?: number;
    lng?: number;
  }
): Promise<AutocompletePrediction[]> {
  const result = await autocomplete({
    path: { org_id: orgId },
    query: {
      input,
      country: options?.country,
      lat: options?.lat,
      lng: options?.lng,
    },
  });

  if (result.data?.data) {
    return result.data.data;
  }

  return [];
}

/**
 * Fetch place details by place ID
 */
export async function getPlaceDetails(
  orgId: string,
  placeId: string
): Promise<PlaceDetails | null> {
  const result = await placeDetails({
    path: { org_id: orgId, place_id: placeId },
  });

  if (result.data?.data) {
    return result.data.data;
  }

  return null;
}

/**
 * Get an address component by type
 */
function getComponent(
  components: AddressComponent[],
  type: string,
  useShortName = false
): string | undefined {
  const component = components.find((c) => c.types.includes(type));
  return component ? (useShortName ? component.short_name : component.long_name) : undefined;
}

/**
 * Detect country code from address components
 */
export function detectCountryCode(components: AddressComponent[]): string {
  const country = getComponent(components, 'country', true);
  return country?.toUpperCase() || 'OTHER';
}

/**
 * Parse address components into typed AddressDetails based on country
 */
export function parseAddressComponents(
  components: AddressComponent[],
  countryCode: string
): AddressDetails {
  const code = countryCode.toUpperCase();

  if (code === 'US') {
    const streetNumber = getComponent(components, 'street_number') || '';
    const route = getComponent(components, 'route') || '';
    const street1 = [streetNumber, route].filter(Boolean).join(' ');

    return {
      type: 'us' as const,
      street1: street1 || getComponent(components, 'street_address') || '',
      street2: getComponent(components, 'subpremise'),
      city: getComponent(components, 'locality') || getComponent(components, 'sublocality') || '',
      state: getComponent(components, 'administrative_area_level_1', true) || '',
      zipCode: getComponent(components, 'postal_code') || '',
    };
  }

  if (code === 'VN') {
    return {
      type: 'vn' as const,
      street: getComponent(components, 'route') || getComponent(components, 'street_address') || '',
      ward: getComponent(components, 'administrative_area_level_3') || getComponent(components, 'sublocality_level_1'),
      district: getComponent(components, 'administrative_area_level_2') || getComponent(components, 'locality') || '',
      province: getComponent(components, 'administrative_area_level_1') || '',
    };
  }

  if (code === 'CA') {
    const streetNumber = getComponent(components, 'street_number') || '';
    const route = getComponent(components, 'route') || '';
    const street1 = [streetNumber, route].filter(Boolean).join(' ');

    return {
      type: 'ca' as const,
      street1: street1 || getComponent(components, 'street_address') || '',
      street2: getComponent(components, 'subpremise'),
      city: getComponent(components, 'locality') || '',
      province: getComponent(components, 'administrative_area_level_1', true) || '',
      postalCode: getComponent(components, 'postal_code') || '',
    };
  }

  // Generic address for other countries
  const lines: string[] = [];
  const streetNumber = getComponent(components, 'street_number');
  const route = getComponent(components, 'route');
  if (streetNumber || route) {
    lines.push([streetNumber, route].filter(Boolean).join(' '));
  }
  const subpremise = getComponent(components, 'subpremise');
  if (subpremise) {
    lines.push(subpremise);
  }

  return {
    type: 'generic' as const,
    lines,
    city: getComponent(components, 'locality') || getComponent(components, 'sublocality'),
    region: getComponent(components, 'administrative_area_level_1'),
    postalCode: getComponent(components, 'postal_code'),
  };
}

/**
 * Build a CreateAddressRequest from place details (without creating in database)
 * Use this for deferred creation scenarios like order creation with new customer
 */
export function buildAddressRequest(
  place: PlaceDetails,
  countryCode: string
): CreateAddressRequest {
  const details = parseAddressComponents(place.address_components, countryCode);

  return {
    details,
    formattedAddress: place.formatted_address,
    latitude: place.location.lat,
    longitude: place.location.lng,
    placeId: place.place_id,
  };
}

/**
 * Create an address from place details
 */
export async function createAddressFromPlace(
  orgId: string,
  place: PlaceDetails,
  countryCode: string
): Promise<Address | null> {
  const request = buildAddressRequest(place, countryCode);

  const result = await createAddress({
    path: { org_id: orgId },
    body: request,
  });

  if (result.data?.data) {
    return result.data.data;
  }

  return null;
}

/**
 * Format an Address for display
 */
export function formatAddressDisplay(address: Address): string {
  return address.formattedAddress;
}
